import {
  ChevronDown,
  ChevronUp,
  CircleCheckBig,
  Clock3,
  MapPin,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import type {
  AutoDormCheckIn,
  AutoDormCheckTask,
  AutoDormCheckTaskStatus,
  AutoDormCheckSettings,
  AutoDormCheckUser,
} from "../types";
import { formatDateTime } from "../utils";

const USER_PAGE_SIZE = 8;
const DETAIL_PAGE_SIZE = 6;
const TASK_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const CHECK_IN_TIME_ZONE_LABEL = "北京时间";

const TASK_STATUS: Record<
  AutoDormCheckTaskStatus,
  { label: string; tone: "neutral" | "clay" | "sage" | "danger" }
> = {
  pending: { label: "等待执行", tone: "neutral" },
  running: { label: "执行中", tone: "clay" },
  succeeded: { label: "已完成", tone: "sage" },
  failed: { label: "失败", tone: "danger" },
  skipped: { label: "已跳过", tone: "neutral" },
  cancelled: { label: "已取消", tone: "neutral" },
};

function taskTimeFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = TASK_TIME_FORMATTERS.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("zh-CN", {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  TASK_TIME_FORMATTERS.set(timeZone, formatter);
  return formatter;
}

function formatTaskTime(task: AutoDormCheckTask): string {
  if (!task.scheduledAt) return "—";
  const date = new Date(task.scheduledAt);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return taskTimeFormatter(task.sourceTimezone || "Asia/Shanghai").format(
      date,
    );
  } catch {
    return formatDateTime(task.scheduledAt);
  }
}

export function AutoDormCheckPage() {
  const [page, setPage] = useState(1);
  const [savingSetting, setSavingSetting] = useState(false);
  const settings = useResource(() => api.autoDormCheckSettings(), []);
  const users = useResource(
    () => api.autoDormCheckUsers({ page, pageSize: USER_PAGE_SIZE }),
    [page],
  );

  const updateGlobalSetting = async (
    key: "entryEnabled" | "functionEnabled",
    enabled: boolean,
  ) => {
    if (savingSetting) return;
    setSavingSetting(true);
    try {
      const next = await api.updateAutoDormCheckSettings({ [key]: enabled });
      settings.setData(next);
      const target = key === "entryEnabled" ? "入口" : "功能";
      toast.success(`${target}已${enabled ? "开启" : "关闭"}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "设置保存失败");
    } finally {
      setSavingSetting(false);
    }
  };

  const refreshAll = () =>
    Promise.all([settings.refresh(), users.refresh()]).then(() => undefined);

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="DORM CHECK"
        title="自动查寝"
        desc="管理功能开放状态、已启用学生、任务与打卡位置"
        className="rise-in"
        action={
          <ActionButton
            busy={settings.refreshing || users.refreshing}
            onClick={() => void refreshAll()}
          >
            <RefreshCw size={13} />
            刷新
          </ActionButton>
        }
      />

      {settings.error && (
        <ErrorState
          error={settings.error}
          onRetry={() => void settings.refresh()}
        />
      )}
      <Panel className="rise-in rise-in-1 overflow-hidden">
        {settings.loading && !settings.data ? (
          <LoadingState rows={3} />
        ) : (
          <>
            <GlobalSwitchRow
              title="小程序入口开关"
              description="关闭后入口隐藏，自动查寝同时停止，功能开关也会关闭；学生个人选择和历史记录仍会保留。"
              enabled={Boolean(settings.data?.entryEnabled)}
              disabled={!settings.data || savingSetting}
              updatedAt={settings.data?.updatedAt}
              onChange={(enabled) =>
                void updateGlobalSetting("entryEnabled", enabled)
              }
            />
            <GlobalSwitchRow
              title="功能可用开关"
              description="关闭后入口仍可显示，但学生端显示“不可用”且不能切换；重新开启后从最近的打卡时段恢复。"
              enabled={Boolean(settings.data?.functionEnabled)}
              disabled={
                !settings.data || savingSetting || !settings.data.entryEnabled
              }
              updatedAt={settings.data?.updatedAt}
              onChange={(enabled) =>
                void updateGlobalSetting("functionEnabled", enabled)
              }
              separated
            />
            <CheckInWindowEditor
              settings={settings.data}
              onSaved={settings.setData}
            />
          </>
        )}
      </Panel>

      <section className="rise-in rise-in-2 space-y-3">
        <div className="flex items-end justify-between gap-4 px-1">
          <div>
            <h3 className="font-display text-[17px] font-medium text-ink">
              已开启学生
            </h3>
            <p className="mt-0.5 text-[12px] text-ink-faint">
              {users.data
                ? `共 ${users.data.pagination.total} 位，仅加载当前页`
                : "只显示个人开关已开启的学生"}
            </p>
          </div>
        </div>

        {users.error && (
          <ErrorState
            error={users.error}
            onRetry={() => void users.refresh()}
          />
        )}
        <Panel className="overflow-hidden">
          {users.loading && !users.data ? (
            <LoadingState rows={8} />
          ) : users.data?.items.length ? (
            <>
              <div className="divide-y divide-sand-light/70">
                {users.data.items.map((user) => (
                  <StudentRow key={user.userId} user={user} />
                ))}
              </div>
              <Pagination
                page={users.data.pagination.page}
                pages={users.data.pagination.totalPages}
                total={users.data.pagination.total}
                onChange={setPage}
                className="border-t border-sand-light/70 px-5 py-4"
              />
            </>
          ) : (
            <EmptyState
              title="暂无已开启学生"
              description="学生在小程序开启自动查寝后会显示在这里。"
            />
          )}
        </Panel>
      </section>
    </div>
  );
}

function GlobalSwitchRow({
  title,
  description,
  enabled,
  disabled,
  updatedAt,
  onChange,
  separated = false,
}: {
  title: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  updatedAt: string | null | undefined;
  onChange: (enabled: boolean) => void;
  separated?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6 ${separated ? "border-t border-sand-light/70" : ""}`}
    >
      <div className="flex min-w-0 gap-3.5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E7EBE0] text-sage">
          <ShieldCheck size={19} />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[14px] font-medium text-ink">{title}</h3>
            <Tag tone={enabled ? "sage" : "neutral"}>
              {enabled ? "已开启" : "已关闭"}
            </Tag>
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">
            {description}
          </p>
          <p className="mt-1 text-[11px] text-ink-faint">
            上次调整 {formatDateTime(updatedAt)}
          </p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={title}
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${enabled ? "bg-sage" : "bg-sand"}`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-paper shadow-sm transition duration-200 ${enabled ? "left-6" : "left-1"}`}
        />
      </button>
    </div>
  );
}

function CheckInWindowEditor({
  settings,
  onSaved,
}: {
  settings: AutoDormCheckSettings | null;
  onSaved: (settings: AutoDormCheckSettings) => void;
}) {
  const startTime = settings?.checkInStartTime ?? "21:00";
  const endTime = settings?.checkInEndTime ?? "23:30";
  const [startDraft, setStartDraft] = useState(startTime);
  const [endDraft, setEndDraft] = useState(endTime);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStartDraft(startTime);
    setEndDraft(endTime);
  }, [startTime, endTime]);

  const changed = startDraft !== startTime || endDraft !== endTime;
  const valid = startDraft < endDraft;

  const save = async () => {
    if (!settings || !changed || !valid || saving) return;
    setSaving(true);
    try {
      const next = await api.updateAutoDormCheckSettings({
        checkInStartTime: startDraft,
        checkInEndTime: endDraft,
      });
      onSaved(next);
      toast.success("打卡时段已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "时段保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-sand-light/70 p-5 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[13px] font-medium text-ink">
            <Clock3 size={15} className="text-clay" />
            自动打卡时段
            <Tag tone="clay">{CHECK_IN_TIME_ZONE_LABEL}</Tag>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-soft">
            时段达到 40 分钟时前后各预留 10 分钟，再随机安排精确到秒的打卡时间。
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <TimeField label="开始" value={startDraft} onChange={setStartDraft} />
          <span className="pb-2 text-[12px] text-ink-faint">至</span>
          <TimeField label="结束" value={endDraft} onChange={setEndDraft} />
          <ActionButton
            busy={saving}
            disabled={!settings || !changed || !valid}
            onClick={() => void save()}
          >
            保存时段
          </ActionButton>
        </div>
      </div>
      {!valid ? (
        <p className="mt-2 text-right text-[11px] text-destructive">
          开始时间必须早于结束时间
        </p>
      ) : null}
    </div>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 text-[11px] text-ink-faint">
      {label}
      <input
        type="time"
        step={60}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-lg border border-sand bg-paper px-3 font-mono text-[12px] text-ink outline-none transition focus:border-clay"
      />
    </label>
  );
}

function StudentRow({ user }: { user: AutoDormCheckUser }) {
  const [expanded, setExpanded] = useState(false);
  const lastCheckIn = user.lastCheckIn;
  return (
    <article>
      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
          <div className="flex min-w-0 flex-1 gap-3.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink font-display text-[13px] text-paper">
              {user.displayName.slice(0, 1)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-[14px] font-medium text-ink">
                  {user.displayName}
                </h4>
                <Tag tone="sage">已开启</Tag>
                {!user.accountActive && <Tag tone="danger">账号停用</Tag>}
              </div>
              <p className="mt-1 font-mono text-[11.5px] text-ink-faint">
                {user.account}
              </p>
              <p className="mt-1 text-[12px] text-ink-soft">
                {[user.grade, user.majorName, user.className]
                  .filter(Boolean)
                  .join(" · ") || "暂无班级资料"}
              </p>
              <p className="mt-1 text-[11px] text-ink-faint">
                开启于 {formatDateTime(user.enabledAt)}
              </p>
            </div>
          </div>

          <div className="grid min-w-0 flex-[1.4] gap-3 md:grid-cols-2">
            <div className="rounded-xl bg-cream/80 p-4">
              <div className="flex items-center gap-2 text-[11.5px] font-medium text-ink-soft">
                <MapPin size={13} className="text-clay" />
                上次打卡位置
              </div>
              {lastCheckIn ? (
                <>
                  <p className="mt-2 truncate text-[13.5px] font-medium text-ink">
                    {lastCheckIn.locationName}
                  </p>
                  <p className="mt-1 text-[11px] text-ink-faint">
                    {formatDateTime(lastCheckIn.checkedAt)}
                  </p>
                  <p className="mt-1 font-mono text-[10.5px] text-ink-faint">
                    {locationDetail(lastCheckIn)}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-[12.5px] text-ink-faint">
                  暂无打卡位置
                </p>
              )}
            </div>

            <div className="rounded-xl bg-cream/80 p-4">
              <div className="flex items-center gap-2 text-[11.5px] font-medium text-ink-soft">
                <Clock3 size={13} className="text-sage" />
                最近任务
              </div>
              {user.recentTasks.length ? (
                <div className="mt-2 space-y-2">
                  {user.recentTasks.map((task) => (
                    <TaskSummary key={task.id} task={task} compact />
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-[12.5px] text-ink-faint">暂无任务</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end border-t border-sand-light/60 pt-3">
          <ActionButton
            tone="ghost"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {expanded ? "收起历史" : "查看全部任务与打卡历史"}
          </ActionButton>
        </div>
      </div>
      {expanded ? <StudentHistory userId={user.userId} /> : null}
    </article>
  );
}

function StudentHistory({ userId }: { userId: string }) {
  const [taskPage, setTaskPage] = useState(1);
  const [checkInPage, setCheckInPage] = useState(1);
  const tasks = useResource(
    () =>
      api.autoDormCheckTasks(userId, {
        page: taskPage,
        pageSize: DETAIL_PAGE_SIZE,
      }),
    [userId, taskPage],
  );
  const checkIns = useResource(
    () =>
      api.autoDormCheckIns(userId, {
        page: checkInPage,
        pageSize: DETAIL_PAGE_SIZE,
      }),
    [userId, checkInPage],
  );

  return (
    <div className="grid gap-5 border-t border-sand-light bg-cream/35 p-5 lg:grid-cols-2 sm:p-6">
      <HistoryPanel title="任务记录" icon={<Clock3 size={14} />}>
        {tasks.error ? (
          <ErrorState
            error={tasks.error}
            onRetry={() => void tasks.refresh()}
          />
        ) : tasks.loading && !tasks.data ? (
          <LoadingState rows={5} />
        ) : tasks.data?.items.length ? (
          <>
            <div className="divide-y divide-sand-light/70">
              {tasks.data.items.map((task) => (
                <TaskSummary key={task.id} task={task} />
              ))}
            </div>
            <Pagination
              page={tasks.data.pagination.page}
              pages={tasks.data.pagination.totalPages}
              total={tasks.data.pagination.total}
              onChange={setTaskPage}
              className="border-t border-sand-light/70 px-4 py-3"
            />
          </>
        ) : (
          <EmptyState title="暂无任务" description="任务生成后会显示在这里。" />
        )}
      </HistoryPanel>

      <HistoryPanel title="打卡历史" icon={<MapPin size={14} />}>
        {checkIns.error ? (
          <ErrorState
            error={checkIns.error}
            onRetry={() => void checkIns.refresh()}
          />
        ) : checkIns.loading && !checkIns.data ? (
          <LoadingState rows={5} />
        ) : checkIns.data?.items.length ? (
          <>
            <div className="divide-y divide-sand-light/70">
              {checkIns.data.items.map((checkIn) => (
                <CheckInSummary key={checkIn.id} checkIn={checkIn} />
              ))}
            </div>
            <Pagination
              page={checkIns.data.pagination.page}
              pages={checkIns.data.pagination.totalPages}
              total={checkIns.data.pagination.total}
              onChange={setCheckInPage}
              className="border-t border-sand-light/70 px-4 py-3"
            />
          </>
        ) : (
          <EmptyState
            title="暂无打卡记录"
            description="成功打卡并记录位置后会显示在这里。"
          />
        )}
      </HistoryPanel>
    </div>
  );
}

function HistoryPanel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-sand-light/80 bg-paper">
      <header className="flex items-center gap-2 border-b border-sand-light/70 px-4 py-3 text-[12.5px] font-medium text-ink">
        <span className="text-clay">{icon}</span>
        {title}
      </header>
      {children}
    </section>
  );
}

function TaskSummary({
  task,
  compact = false,
}: {
  task: AutoDormCheckTask;
  compact?: boolean;
}) {
  const status = TASK_STATUS[task.status];
  return (
    <div className={compact ? "flex items-center gap-2" : "px-4 py-3"}>
      <div
        className={
          compact ? "min-w-0 flex-1" : "flex items-center justify-between gap-3"
        }
      >
        <p className="truncate text-[11.5px] text-ink-soft">
          {formatTaskTime(task)}
        </p>
        <Tag tone={status.tone}>{status.label}</Tag>
      </div>
      {!compact && task.failureMessage ? (
        <p className="mt-1 text-[11px] text-destructive">
          {task.failureMessage}
          {task.failureCode ? ` · ${task.failureCode}` : ""}
        </p>
      ) : null}
    </div>
  );
}

function CheckInSummary({ checkIn }: { checkIn: AutoDormCheckIn }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <CircleCheckBig size={15} className="mt-0.5 shrink-0 text-sage" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium text-ink">
            {checkIn.locationName}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-faint">
            {formatDateTime(checkIn.checkedAt)}
          </p>
          <p className="mt-1 font-mono text-[10.5px] text-ink-faint">
            {locationDetail(checkIn)} · 任务 #{checkIn.taskId}
          </p>
        </div>
      </div>
    </div>
  );
}

function locationDetail(checkIn: AutoDormCheckIn) {
  if (checkIn.latitude === null || checkIn.longitude === null) {
    return checkIn.accuracyMeters === null
      ? "未记录坐标"
      : `精度 ${checkIn.accuracyMeters.toFixed(1)} m`;
  }
  const coordinates = `${checkIn.latitude.toFixed(6)}, ${checkIn.longitude.toFixed(6)}`;
  return checkIn.accuracyMeters === null
    ? coordinates
    : `${coordinates} · 精度 ${checkIn.accuracyMeters.toFixed(1)} m`;
}
